import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, asRecord, field, listFrom } from '../api';
import { EvidenceButton } from '../components/evidence';
import { Icon } from '../components/icons';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  SectionHeader,
  Stat,
  StatusBadge,
} from '../components/ui';
import type { Decision, ResearchResult, Responsibility } from '../types';
import { formatDate, humanize, isPast, relativeDate, responsibilityField } from '../utils';
import { topLevelModules } from '../module-catalog';

const resilienceItems = [
  'Emergency liquidity',
  'Essential insurance',
  'Emergency contacts',
  'Will / incapacity',
  'Critical-account MFA',
  'Backup restore',
  'Pension record',
];

export function MissionControlPage(): React.JSX.Element {
  const bootstrap = useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api<unknown>('/bootstrap'),
  });
  const responsibilitiesQuery = useQuery({
    queryKey: ['responsibilities', 'mission'],
    queryFn: () => api<unknown>('/responsibilities?limit=100'),
  });
  const decisionsQuery = useQuery({
    queryKey: ['decisions', 'mission'],
    queryFn: () => api<unknown>('/decisions?limit=50'),
  });
  const researchQuery = useQuery({
    queryKey: ['research', 'stale'],
    queryFn: () => api<unknown>('/research/search?kind=claim&stale=true&limit=30'),
  });
  const responsibilities = listFrom<Responsibility>(responsibilitiesQuery.data, [
    'responsibilities',
  ]);
  const decisions = listFrom<Decision>(decisionsQuery.data, ['decisions']);
  const research = listFrom<ResearchResult>(researchQuery.data, ['results', 'claims']);
  const bootstrapData = asRecord(bootstrap.data);
  const kpis = asRecord(field(bootstrapData, 'kpis', 'metrics'));
  const committed = responsibilities
    .filter((item) => ['next', 'in_progress'].includes(item.status))
    .slice(0, 5);
  const waiting = responsibilities.filter((item) => ['waiting', 'blocked'].includes(item.status));
  const upcoming = responsibilities
    .filter((item) => responsibilityField<string | null>(item, 'deadlineEnd', 'deadline_end'))
    .sort((a, b) =>
      String(responsibilityField(a, 'deadlineEnd', 'deadline_end')).localeCompare(
        String(responsibilityField(b, 'deadlineEnd', 'deadline_end')),
      ),
    )
    .slice(0, 7);
  const hard = responsibilities.filter(
    (item) =>
      responsibilityField(item, 'deadlineKind', 'deadline_kind') === 'hard' &&
      !['done', 'closed'].includes(item.status),
  );
  const nextHard = hard
    .filter((item) => !isPast(responsibilityField(item, 'deadlineEnd', 'deadline_end')))
    .sort((a, b) =>
      String(responsibilityField(a, 'deadlineEnd', 'deadline_end')).localeCompare(
        String(responsibilityField(b, 'deadlineEnd', 'deadline_end')),
      ),
    )[0];
  const overdueHard = hard.filter((item) =>
    isPast(responsibilityField(item, 'deadlineEnd', 'deadline_end')),
  );
  const protectionData = asRecord(
    field(kpis, 'deadlineProtection', 'deadlineProtection90Day', 'deadline_protection_90_day'),
  );
  const protection =
    field<number | string>(protectionData, 'display', 'percentage') ?? 'No applicable deadlines';
  const coverageData = asRecord(field(kpis, 'currentSourceCoverage', 'current_source_coverage'));
  const sourceCoverage = field<number>(coverageData, 'percentage') ?? '—';
  const modules = listFrom<Record<string, unknown>>(
    field(bootstrapData, 'modules', 'moduleHealth'),
    ['modules'],
  );
  const moduleBySlug = new Map(
    modules.map((module) => [field<string>(module, 'slug', 'moduleId', 'module_id'), module]),
  );
  if (responsibilitiesQuery.isLoading || bootstrap.isLoading)
    return <LoadingBlock label="Assembling Mission Control" />;
  if (responsibilitiesQuery.isError)
    return (
      <ErrorBlock
        error={responsibilitiesQuery.error}
        onRetry={() => void responsibilitiesQuery.refetch()}
      />
    );
  return (
    <div className="page mission-control">
      <PageHeader
        eyebrow="Thursday · 13 August 2026 · Europe/Berlin"
        title="What must I do next?"
        description="Protect the critical path, keep decisions evidence-ready, and limit committed work to five items."
        actions={
          <>
            <Link className="button button--default" to="/roadmap">
              <Icon name="roadmap" size={16} />
              Open roadmap
            </Link>
            <Link className="button button--primary" to="/responsibilities?new=true">
              <Icon name="plus" size={16} />
              Add responsibility
            </Link>
          </>
        }
      />

      <section className="hero-kpis" aria-label="Deadline and evidence KPIs">
        <Card className="hero-deadline">
          <p className="eyebrow">1 · Next confirmed hard deadline</p>
          {nextHard ? (
            <>
              <div className="hero-deadline__date">
                <strong>
                  {formatDate(responsibilityField(nextHard, 'deadlineEnd', 'deadline_end'), {
                    day: '2-digit',
                    month: 'short',
                  })}
                </strong>
                <span>
                  {relativeDate(responsibilityField(nextHard, 'deadlineEnd', 'deadline_end'))}
                </span>
              </div>
              <h2>{nextHard.title}</h2>
              <p>
                {responsibilityField(nextHard, 'nextAction', 'next_action') ??
                  'Next action needs to be recorded.'}
              </p>
              <div className="hero-deadline__meta">
                <Badge tone="danger">Hard deadline</Badge>
                <Badge>
                  {responsibilityField(nextHard, 'deadlineConfidence', 'deadline_confidence')}
                </Badge>
                <Link to={`/responsibilities?focus=${nextHard.id}`}>
                  Open responsibility <Icon name="arrow" size={14} />
                </Link>
              </div>
            </>
          ) : (
            <EmptyState
              title="No confirmed hard deadline"
              description="No future authority-confirmed hard deadline is currently available."
            />
          )}
        </Card>
        <div className="hero-kpi-stack">
          <Stat
            label="2 · Overdue hard deadlines"
            value={overdueHard.length}
            meta={overdueHard.length ? 'Needs immediate triage' : 'No overdue confirmed deadlines'}
            tone={overdueHard.length ? 'danger' : 'success'}
          />
          <Stat
            label="3 · 90-day deadline protection"
            value={typeof protection === 'number' ? `${protection}%` : protection}
            meta="Owner · action · date · source · gate"
            tone="brand"
          />
        </div>
        <Card className="source-coverage-card">
          <p className="eyebrow">4 · Current-source coverage</p>
          <div className="source-coverage__value">
            <strong>
              {typeof sourceCoverage === 'number' ? `${sourceCoverage}%` : sourceCoverage}
            </strong>
            <span>Tier 1–2 current sources</span>
          </div>
          <div className="progress-track">
            <i style={{ width: `${typeof sourceCoverage === 'number' ? sourceCoverage : 0}%` }} />
          </div>
          <p>Freshness supports planning; it does not guarantee a legal conclusion.</p>
          <Link to="/sources">
            Review source register <Icon name="arrow" size={14} />
          </Link>
        </Card>
      </section>

      <section className="mission-grid mission-grid--focus">
        <Card className="focus-queue">
          <SectionHeader
            eyebrow="5 · Committed work"
            title="Focus queue"
            description={`${committed.length} of 5 WIP slots in use`}
            action={<Link to="/responsibilities?filter=focus">View all</Link>}
          />
          <div className="wip-dots" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <i key={index} className={index < committed.length ? 'is-used' : ''} />
            ))}
          </div>
          {committed.length ? (
            <ol className="focus-list">
              {committed.map((item, index) => (
                <li key={item.id}>
                  <span className="focus-list__number">0{index + 1}</span>
                  <div>
                    <div className="focus-list__title">
                      <Link to={`/responsibilities?focus=${item.id}`}>{item.title}</Link>
                      <StatusBadge status={item.status} />
                    </div>
                    <p>{responsibilityField(item, 'nextAction', 'next_action')}</p>
                    <div className="row-meta">
                      <span>
                        <Icon name="calendar" size={14} />
                        {formatDate(responsibilityField(item, 'deadlineEnd', 'deadline_end'))}
                      </span>
                      <span>{item.priority}</span>
                      <span>{responsibilityField(item, 'ownerRole', 'owner_role')}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyState
              title="No committed work"
              description="Move up to five responsibilities into Next or In progress."
            />
          )}
        </Card>
        <Card className="followups">
          <SectionHeader
            eyebrow="6 · External dependencies"
            title="Waiting & blocked"
            description={`${waiting.length} follow-up${waiting.length === 1 ? '' : 's'}`}
          />
          {waiting.length ? (
            <div className="compact-list">
              {waiting.slice(0, 6).map((item) => (
                <Link to={`/responsibilities?focus=${item.id}`} key={item.id}>
                  <span className={`status-pin status-pin--${item.status}`} />
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.status === 'waiting'
                        ? `Waiting on ${responsibilityField<string>(item, 'waitingOn', 'waiting_on') ?? 'unknown'}`
                        : (responsibilityField<string>(item, 'blockedReason', 'blocked_reason') ??
                          'Blocked reason missing')}
                    </small>
                  </div>
                  <span>
                    {item.status === 'waiting'
                      ? formatDate(responsibilityField(item, 'followUpAt', 'follow_up_at'))
                      : 'Review'}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Nothing waiting"
              description="No blocked or externally waiting responsibilities."
            />
          )}
        </Card>
      </section>

      <section className="mission-grid mission-grid--lower">
        <Card>
          <SectionHeader
            eyebrow="7 · Time horizon"
            title="Upcoming timeline"
            action={<Link to="/roadmap">Full roadmap</Link>}
          />
          {upcoming.length ? (
            <div className="timeline-list">
              {upcoming.map((item) => (
                <Link to={`/responsibilities?focus=${item.id}`} key={item.id}>
                  <time>
                    {formatDate(responsibilityField(item, 'deadlineEnd', 'deadline_end'), {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </time>
                  <i />
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {responsibilityField(item, 'deadlineKind', 'deadline_kind')} ·{' '}
                      {responsibilityField(item, 'deadlineConfidence', 'deadline_confidence')}
                    </small>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Timeline is clear"
              description="No dated responsibilities returned."
            />
          )}
        </Card>
        <Card>
          <SectionHeader
            eyebrow="8 · Decision load"
            title="Open decisions"
            action={<Link to="/decisions">Decision register</Link>}
          />
          {decisions.filter((item) => !['decided', 'deferred'].includes(item.status)).length ? (
            <div className="decision-list">
              {decisions
                .filter((item) => !['decided', 'deferred'].includes(item.status))
                .slice(0, 5)
                .map((item) => (
                  <Link to={`/decisions/${item.id}`} key={item.id}>
                    <span>
                      <StatusBadge status={item.status} />
                      <strong>{item.title}</strong>
                    </span>
                    <Icon name="chevron" size={15} />
                  </Link>
                ))}
            </div>
          ) : (
            <EmptyState
              title="No open decisions"
              description="Decision dossiers will appear here when active."
            />
          )}
        </Card>
        <Card>
          <SectionHeader
            eyebrow="9 · Evidence maintenance"
            title="Stale research alerts"
            action={<Link to="/sources?freshness=stale">Refresh queue</Link>}
          />
          {research.length ? (
            <div className="research-alerts">
              {research.slice(0, 5).map((item, index) => {
                const id = item.id ?? item.claim_id ?? item.source_id ?? String(index);
                const title = item.title ?? item.safe_render ?? id;
                return (
                  <div key={id}>
                    <span className="alert-icon">
                      <Icon name="warning" size={15} />
                    </span>
                    <div>
                      <strong>{title}</strong>
                      <small>
                        {item.jurisdiction ?? 'Multi-jurisdiction'} · review{' '}
                        {formatDate(item.next_review)}
                      </small>
                    </div>
                    <EvidenceButton item={item} title={title} label="Inspect" />
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="No stale alerts"
              description="The API returned no stale research items."
            />
          )}
        </Card>
      </section>

      <section className="baseline-section">
        <SectionHeader
          eyebrow="10 · Whole-life coverage"
          title="Module health & resilience baseline"
          description="Review completion, not a combined life score."
        />
        <div className="module-strip">
          {topLevelModules.map(({ slug, title }) => {
            const module = moduleBySlug.get(slug);
            const state = field<string>(module, 'status') ?? 'not_assessed';
            return (
              <Link to={`/modules/${slug}`} key={slug}>
                <span className={`module-state module-state--${state}`} />
                <strong>{title}</strong>
                <small>{humanize(state)}</small>
              </Link>
            );
          })}
        </div>
        <Card className="resilience-baseline">
          <div>
            <Icon name="shield" />
            <span>
              <strong>Resilience baseline</strong>
              <small>Assessed foundations</small>
            </span>
          </div>
          <div className="resilience-checks">
            {resilienceItems.map((item) => (
              <span key={item}>
                <i />
                <small>{item}</small>
              </span>
            ))}
          </div>
          <Button variant="quiet" icon="arrow">
            Review baseline
          </Button>
        </Card>
      </section>
    </div>
  );
}
